import {
  type Catalog,
  type CompanyCatalogMeta,
  CompanyCatalogApi,
  GroupsApi,
  TagCategoryEnum,
  TagsApi,
  TrainingsApi,
  ItemType as SdkItemType,
} from "@simbelapp/order-sdk";
import { defineStore } from "pinia";
import { useSDKApi } from "~/composables/api/useSDKApi";
import { useFilters } from "~/composables/filters/useFilters";
import { usePagination } from "~/composables/paginations/usePagination";
import { useAuthStore } from "~/store/auth/auth.store";
import { MicroserviceEnum } from "~/utils/enums/common.enums";
import { TrainingStatus, VisibleStatus } from "~/utils/enums/training.enums";
import type { IVisibility } from "~/utils/interfaces/trainings-interface";
import { useFeedback } from "~/composables/feedback/useFeedback";
import type { ICatalogStore } from "~/utils/interfaces/catalog-interfaces";

// Builds the company-catalog export request from the current list state: pagination + active filters
// (+ LIVE status, mirroring the list's `isLive`). `my_trainings` is only included when the "mes
// formations" view is active — the backend scopes to the caller's own trainings whenever the param is
// present (even `false`), so sending `false` would wrongly shrink the export.
function buildCatalogExportParams(myTrainings: boolean) {
  const pagination = usePagination();
  const filters = useFilters();

  const paginateOptions = pagination.getFindOptions(pagination.paginateOptions.value);
  delete paginateOptions.offset;
  delete paginateOptions.limit;

  const queryString = Object.fromEntries(new URLSearchParams(paginateOptions).entries());
  const { filters: sdkFilters } = filters.getFiltersForSDK();
  sdkFilters.push(`training_status=${TrainingStatus.LIVE}`);

  return { ...queryString, filters: sdkFilters, ...(myTrainings ? { myTrainings: true } : {}) };
}

// Forwards the list's active filters + search so a "select all" mutation resolves the same scope
// the user sees (the backend recomputes the target set from these when selectAll is true).
function buildCatalogScopeParams(): { filters: string[]; search?: string } {
  const pagination = usePagination();
  const filters = useFilters();

  const paginateOptions = pagination.getFindOptions(pagination.paginateOptions.value);
  const { filters: sdkFilters } = filters.getFiltersForSDK();

  return { filters: sdkFilters, search: paginateOptions.search };
}

export const useCatalogStore = defineStore("catalogStore", {
  state: (): ICatalogStore => {
    return {
      employeesCatalog: null,
      showCsvImportModal: false,
      showCsvExportModal: false,
      companyCatalog: null,
      count: {
        trainings: 0,
        _exists: 0,
      },
      deleted: 0,
      selectedItems: [],
      excludedItems: [],
      selectedAll: false,
      showDeleteCatalog: false,
      showEditTagModal: false,
      showVisibilityModal: false,
    };
  },
  getters: {},
  actions: {
    // Searches the company catalog through the SDK, returning the SDK Catalog[] shape (camelCase)
    // directly. Used as the `servicesAPI` backing the training-search dropdowns.
    async searchCompanyCatalog(
      params: Record<string, any> = {},
    ): Promise<{ data: Catalog[]; meta: CompanyCatalogMeta | null }> {
      const feedback = useFeedback();
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, CompanyCatalogApi);

      try {
        const response = await apiInstance.getCompanyCatalog(params);
        return {
          data: response.data ?? [],
          meta: response.meta,
        };
      } catch (error) {
        feedback.error(`Une erreur est survenue`, "small");
        return { data: [], meta: null };
      }
    },

    async fetchEmployeesCatalog(trainingId?: string) {
      if (trainingId) {
        const { data } = await this.searchCompanyCatalog({ trainingId, limit: 10 });
        this.employeesCatalog = data;
        return;
      }

      // Scope visibility to the user's groups when available (mirrors the legacy only_visible param)
      const auth = useAuthStore();
      let onlyVisible = "only_visible=visible";
      if (auth.user?.userId) {
        try {
          const groupsApi = await useSDKApi(MicroserviceEnum.ORDERS, GroupsApi);
          const groups = await groupsApi.getUserGroups({ userId: auth.user.userId });
          const groupIds = groups?.attachedGroups?.map((group) => group.groupId) ?? [];
          if (groupIds.length) {
            onlyVisible = `only_visible=visible,${groupIds.join(",")}`;
          }
        } catch {
          // best-effort group scoping; fall back to visible-only
        }
      }

      const { data } = await this.searchCompanyCatalog({
        limit: "custom",
        filters: [`training_status=${TrainingStatus.LIVE}`, "my_catalog=true", onlyVisible],
      });
      this.employeesCatalog = data;
    },

    async fetchCompanyCatalog(myTrainings?: boolean) {
      const pagination = usePagination();
      const filters = useFilters();
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, CompanyCatalogApi);
      const feedback = useFeedback();

      const paginateOptions = pagination.getFindOptions(pagination.paginateOptions.value);
      const paginateQuery = new URLSearchParams(paginateOptions);
      const queryString = Object.fromEntries(paginateQuery.entries());
      const { filters: sdkFilters } = filters.getFiltersForSDK();

      try {
        this.companyCatalog = await apiInstance.getCompanyCatalog({
          ...queryString,
          sortingColumn: paginateOptions.sortingColumn,
          filters: [...sdkFilters, `training_status=${TrainingStatus.LIVE}`],
          myTrainings,
        });
      } catch (error) {
        feedback.error(`Une erreur est survenue`, "small");
        throw error;
      }
    },

    async changeVisibility(
      trainingIds: string[],
      visibility: IVisibility,
      selectedAll: boolean = false,
      replaceGroups: boolean = false,
      noFilters: boolean = false,
    ): Promise<void> {
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, CompanyCatalogApi);
      const feedback = useFeedback();

      const visible = visibility.visible === VisibleStatus.GROUPS || visibility.visible === VisibleStatus.ALL;
      // Support both object format and legacy string format
      const groupIds = visibility.groups?.map((g: any) => (typeof g === "string" ? g : g.groupId));

      try {
        await apiInstance.updateTrainingVisibility({
          changeTrainingVisibilityDto: { trainingIds, visible, selectedAll, replaceGroups, groupIds },
          ...(noFilters ? {} : buildCatalogScopeParams()),
        });
      } catch {
        feedback.error(`Une erreur est survenue`, "small");
      }
    },

    async addTrainingToCatalog(selectedIds: string[], selectAll: boolean, excludedIds?: string[]): Promise<void> {
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, CompanyCatalogApi);
      const feedback = useFeedback();

      try {
        const res = await apiInstance.addTrainingToCatalog({
          catalogTrainingSelectionDto: { selectedIds: selectAll ? [] : selectedIds, excludedIds, selectAll },
          ...buildCatalogScopeParams(),
        });
        this.count = res.count;
      } catch {
        feedback.error(`Une erreur est survenue`, "small");
      }
    },

    async deleteTrainingFromCatalog(selectedIds: string[], selectAll: boolean, excludedIds?: string[]): Promise<void> {
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, CompanyCatalogApi);
      const feedback = useFeedback();

      try {
        const res = await apiInstance.deleteTrainingFromCatalog({
          catalogTrainingSelectionDto: { selectedIds: selectAll ? [] : selectedIds, excludedIds, selectAll },
          ...buildCatalogScopeParams(),
        });
        this.deleted = res.count.trainings;
      } catch {
        feedback.error(`Une erreur est survenue`, "small");
      }
    },

    // Returns the id of the freshly created copy so the caller can open it.
    async duplicateTraining(trainingId: string): Promise<string | null> {
      const feedback = useFeedback();

      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, TrainingsApi);

        const response = await apiInstance.duplicateTrainingWithContent({ trainingId });

        feedback.success(`La formation a bien été dupliquée`, "small");
        return response?.trainingId ?? null;
      } catch (error) {
        feedback.error(`Une erreur est survenue`, "small");
        return null;
      }
    },

    async batchAssignTrainingTags(
      selectAll: boolean,
      itemType: SdkItemType,
      tagIds: string[],
      tagCategory: TagCategoryEnum,
      itemIds?: string[],
    ) {
      const pagination = usePagination();
      const filters = useFilters();
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, TagsApi);
      const paginateOptions = pagination.getFindOptions(pagination.paginateOptions.value);
      const paginateQuery = new URLSearchParams(paginateOptions);
      const queryString = Object.fromEntries(paginateQuery.entries());
      const filtersQuery = filters.getFiltersForSDK();
      try {
        await apiInstance.batchAssignTagsToTrainings({
          batchAssignTagsDto: {
            selectAll,
            tagIds,
            itemIds: itemIds ?? [],
            tagCategory,
            itemType,
          },
          ...queryString,
          ...filtersQuery,
        });
      } catch {
        // feedback.error(`Une erreur est survenue`, "small");
      }
    },

    // Triggers an async CSV export of the company catalog (current filters/search, delivered by email).
    // Returns true on success so callers can gate the confirmation modal.
    async exportCompanyCatalogCsv(myTrainings = false): Promise<boolean> {
      const feedback = useFeedback();
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, CompanyCatalogApi);
      try {
        await apiInstance.exportCompanyCatalogCSVLink(buildCatalogExportParams(myTrainings));
        return true;
      } catch (error) {
        feedback.error(`Erreur lors de l'export CSV du catalogue`, "small");
        return false;
      }
    },

    // Triggers an async XLSX export of the company catalog (current filters/search, delivered by email).
    // Returns true on success so callers can gate the confirmation modal.
    async exportCompanyCatalogXlsx(myTrainings = false): Promise<boolean> {
      const feedback = useFeedback();
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, CompanyCatalogApi);
      try {
        await apiInstance.exportCompanyCatalogXLSXLink(buildCatalogExportParams(myTrainings));
        return true;
      } catch (error) {
        feedback.error(`Erreur lors de l'export XLSX du catalogue`, "small");
        return false;
      }
    },
  },
});
